import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { formatDate } from '../lib/date';
import { usePortfolioScroll } from '../lib/usePortfolioScroll';
import type { Project } from '../lib/types';
import SubHeader from '../components/SubHeader';
import ScrollIcon from '../components/ScrollIcon';
import DateSidebar from '../components/DateSidebar';
import Icon from '../components/Icon';

interface FilterTag {
  name: string;
  selected: boolean;
}

/**
 * Portfolio gallery. Fetches the auth-gated /api/projects endpoint, builds a
 * tag filter sidebar, renders sorted (newest-first) project cards, and wires
 * up GSAP-driven scroll animations:
 *   • Each card animates in (title/date/image/description/demo/tags staggered)
 *   • A fixed date-range column on the left scales the month/year of the
 *     project currently in view up smoothly.
 *
 * Auth: route is wrapped in <RequireAuth /> so unauthenticated users are bounced
 * to /welcome before reaching this component. The fetch itself is also 401-gated
 * server-side, so the project data is never shipped to anonymous clients.
 */
export default function PortfolioPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [tags, setTags] = useState<FilterTag[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const projectRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    let cancelled = false;
    api.getProjects().then((d) => {
      if (cancelled) return;
      const list = d.projects ?? [];
      setProjects(list);
      const tagSet = new Set<string>();
      for (const p of list) for (const t of p.tags ?? []) tagSet.add(t);
      setTags([...tagSet].sort().map((name) => ({ name, selected: false })));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedTags = useMemo(() => tags.filter((t) => t.selected).map((t) => t.name), [tags]);

  const toggleTag = useCallback((name: string) => {
    setTags((prev) =>
      prev.map((t) => (t.name === name ? { ...t, selected: !t.selected } : t)),
    );
  }, []);

  const filteredProjects = useMemo(() => {
    const sorted = [...projects].sort((a, b) => (a.date < b.date ? 1 : -1));
    if (selectedTags.length === 0) return sorted;
    return sorted.filter((p) => selectedTags.every((tag) => (p.tags ?? []).includes(tag)));
  }, [projects, selectedTags]);

  // Reset the project ref array whenever the filtered list changes so refs
  // line up with the new render order. Each ProjectCard will assign to the
  // appropriate slot during render.
  projectRefs.current = projectRefs.current.slice(0, filteredProjects.length);

  // Memoize one ref callback per index so the reference is stable across
  // re-renders. With an inline `ref={(el) => projectRefs.current[i] = el}`,
  // React saw a new function every render and would attach/detach the ref
  // each time — and the resulting DOM commits were enough to abort the
  // CSS entrance animation on the project cards.
  const setProjectRef = useMemo(
    () =>
      Array.from({ length: filteredProjects.length }, (_, i) => (el: HTMLDivElement | null) => {
        projectRefs.current[i] = el;
      }),
    [filteredProjects.length],
  );

  // Sidebar dates (one per project, in scroll order). Empty string is replaced
  // by a placeholder so each project keeps its own entry.
  const sidebarDates = useMemo(
    () => filteredProjects.map((p) => formatDate(p.date, 'shortMonthYear') || '—'),
    [filteredProjects],
  );

  const { activeIndex, scrollToIndex } = usePortfolioScroll(containerRef, projectRefs, [
    filteredProjects.length,
    // Project IDs as a single string — invalidates when filter shuffles the order.
    filteredProjects.map((p) => p.project_id ?? p.title).join(','),
  ]);

  // Sorted tag list — selected first, then alphabetical — matches the old behaviour.
  const sortedTags = useMemo(
    () =>
      [...tags].sort((a, b) => {
        if (a.selected !== b.selected) return a.selected ? -1 : 1;
        return a.name.localeCompare(b.name);
      }),
    [tags],
  );

  return (
    <>
      <SubHeader
        title="PORTFOLIO"
        right={
          <a
            href="#"
            className={filtersVisible ? 'active' : ''}
            onClick={(e) => {
              e.preventDefault();
              setFiltersVisible(true);
            }}
          >
            <Icon name="filter" /> <span className="hidden-xs">FILTER</span>
            {selectedTags.length > 0 && <span> ({selectedTags.length})</span>}
          </a>
        }
      />

      {filtersVisible && (
        <div className="portfolio-filter">
          <button className="close" onClick={() => setFiltersVisible(false)}>
            X
          </button>
          <h2>Filter by Technology:</h2>
          <div className="filter-content">
            {sortedTags.map((tag, idx) => (
              <button
                key={tag.name}
                className={`filter-toggle filter-tag${tag.selected ? ' selected' : ''}`}
                style={{ top: `${idx * 30}px` }}
                onClick={() => toggleTag(tag.name)}
              >
                {tag.name}{' '}
                <Icon name={tag.selected ? 'check' : 'check-empty'} />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="site-page with-subheader portfolio-page" ref={containerRef}>
        <div>
          <div className="container">
            <div className="row">
              <div className="col-sm-2 date-range-col hidden-xs">
                <DateSidebar
                  dates={sidebarDates}
                  activeIndex={activeIndex}
                  onSelect={scrollToIndex}
                />
              </div>
              <div className="col-sm-10">
                {filteredProjects.map((project, i) => (
                  <ProjectCard
                    key={`${project.project_id ?? project.title}-${i}`}
                    ref={setProjectRef[i]}
                    project={project}
                    selectedTags={selectedTags}
                    onToggleTag={toggleTag}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="scroll-icon-container">
            <div className="container">
              <div className="row">
                <div className="col-sm-2"></div>
                <div className="col-sm-10">
                  <ScrollIcon />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

interface ProjectCardProps {
  project: Project;
  selectedTags: string[];
  onToggleTag: (name: string) => void;
}

/**
 * Entrance-animation style helper.
 *
 * Inline styles driven by React state — when `loaded` flips, the browser
 * runs the transition on its own. The style object is recreated each
 * render but the property *values* are identical, so React's reconciler
 * doesn't touch the DOM, and the transition isn't interrupted.
 *
 * Animates BOTH a slide-in transform AND an opacity fade. The fade was
 * surprisingly brittle to get working in dev (CSS class toggles kept
 * being aborted by React's renders) — inline styles fixed it because
 * React only mutates the inline style attribute on the single render
 * where `loaded` changes from false to true.
 */
function entranceStyle(loaded: boolean, hiddenTransform: string, delay: number): React.CSSProperties {
  return {
    opacity: loaded ? 1 : 0,
    transform: loaded ? 'translate3d(0, 0, 0)' : hiddenTransform,
    transition: `opacity 0.6s cubic-bezier(0.2, 0.8, 0.3, 1) ${delay}s, transform 0.6s cubic-bezier(0.2, 0.8, 0.3, 1) ${delay}s`,
  };
}

const ProjectCard = forwardRef<HTMLDivElement, ProjectCardProps>(function ProjectCard(
  { project, selectedTags, onToggleTag },
  ref,
) {
  const localRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  useImperativeHandle(ref, () => localRef.current as HTMLDivElement, []);

  useEffect(() => {
    const el = localRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoaded(true);
          obs.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="project clearfix" ref={localRef}>
      <h3
        style={entranceStyle(loaded, 'translateY(40px)', 0)}
        dangerouslySetInnerHTML={{ __html: project.title }}
      />
      {project.image && (
        <div className="image" style={entranceStyle(loaded, 'translateX(60px)', 0.15)}>
          <img src={project.image} alt="" />
        </div>
      )}
      <h4 className="date" style={entranceStyle(loaded, 'translateY(20px)', 0.2)}>
        {formatDate(project.date, 'shortMonthYear')}
      </h4>
      <div
        className="description"
        style={entranceStyle(loaded, 'translateY(30px)', 0.1)}
        dangerouslySetInnerHTML={{ __html: project.description }}
      />
      {project.demo && (
        <div className="demo" style={entranceStyle(loaded, 'translateY(15px)', 0.45)}>
          <Icon name="globe" />{' '}
          <a href={project.demo} target="_blank" rel="noreferrer">
            Live Example
          </a>
          {project.demo_note && <span> - {project.demo_note}</span>}
        </div>
      )}
      <div className="tags" style={entranceStyle(loaded, 'translateY(15px)', 0.55)}>
        {(project.tags ?? []).map((tag) => {
          const filtered = selectedTags.includes(tag);
          return (
            <a
              key={tag}
              className={`tag${filtered ? ' filtered' : ''}`}
              title={
                filtered
                  ? `Unfilter by ${tag}`
                  : `Click to filter projects which include ${tag}` +
                    (selectedTags.length ? ` (and ${selectedTags.join(', ')})` : '')
              }
              onClick={(e) => {
                e.preventDefault();
                onToggleTag(tag);
              }}
            >
              <Icon name="tag" /> {tag}
            </a>
          );
        })}
      </div>
    </div>
  );
});
